// NotificationBell.js — top-bar alert bell.
// Surfaces three action signals for admins/managers, AUDITOR REQUESTS FIRST:
//   1. Open auditor evidence requests  (/auditor/evidence-requests?open_only=true)
//   2. Pending document approvals       (/vault/pending-tasks/count)
//   3. Open security findings           (/connectors/findings?status=open)
// Frontend-only: reuses existing endpoints, no new backend. Every fetch is
// independent + non-fatal so one failing signal never blanks the bell.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bell } from 'lucide-react';
import api from '../services/api';
import { auditService } from '../services/auditService';

export default function NotificationBell() {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [open, setOpen] = useState(false);
    const [approvals, setApprovals] = useState(0);
    const [findings, setFindings] = useState(0);
    const [auditReqs, setAuditReqs] = useState([]);
    const ref = useRef(null);
    const base = `/t/${tenantId}`;

    const load = useCallback(async () => {
        try { const r = await api.get('/vault/pending-tasks/count'); setApprovals(r.data?.count || 0); } catch (e) { /* non-fatal */ }
        try { const r = await api.get('/connectors/findings?status=open&limit=50'); setFindings((r.data?.findings || []).length); } catch (e) { /* non-fatal */ }
        try {
            const reqs = await auditService.getEvidenceRequests(true);
            setAuditReqs(Array.isArray(reqs) ? reqs : (reqs?.requests || []));
        } catch (e) { /* non-fatal */ }
    }, []);

    useEffect(() => {
        if (!tenantId) return undefined;
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [tenantId, load]);

    // Close on outside click.
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const total = approvals + findings + auditReqs.length;
    const go = (path) => { setOpen(false); navigate(path); };

    return (
        <div className="relative" ref={ref}>
            <button onClick={() => setOpen((o) => !o)}
                className="relative p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
                aria-label="Notifications">
                <Bell className="w-5 h-5" />
                {total > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {total > 99 ? '99+' : total}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-bold text-gray-800">Notifications</div>
                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">

                        {/* Auditor requests — priority signal, shown first with detail */}
                        {auditReqs.length > 0 && (
                            <div className="py-1">
                                <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-600">
                                    Auditor Requests
                                </div>
                                {auditReqs.slice(0, 6).map((r) => (
                                    <button key={r.id} onClick={() => go(`${base}/audit-requests`)}
                                        className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors">
                                        <p className="text-xs font-semibold text-gray-800 truncate">
                                            Evidence requested: {r.control_id || r.requirement_key || 'requirement'}
                                        </p>
                                        <p className="text-[11px] text-gray-500 truncate">
                                            {(r.auditor_name || 'Auditor')}{r.auditor_message ? ` · ${r.auditor_message}` : ''}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        )}

                        {approvals > 0 && (
                            <button onClick={() => go(`${base}/vault`)}
                                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between transition-colors">
                                <span className="text-xs text-gray-700">{approvals} pending approval{approvals !== 1 ? 's' : ''}</span>
                                <span className="text-[11px] text-blue-600 font-semibold">Review →</span>
                            </button>
                        )}

                        {findings > 0 && (
                            <button onClick={() => go(`${base}/dashboard`)}
                                className="w-full text-left px-4 py-2.5 hover:bg-amber-50 flex items-center justify-between transition-colors">
                                <span className="text-xs text-gray-700">{findings}{findings >= 50 ? '+' : ''} open security finding{findings !== 1 ? 's' : ''}</span>
                                <span className="text-[11px] text-blue-600 font-semibold">View →</span>
                            </button>
                        )}

                        {total === 0 && (
                            <div className="px-4 py-6 text-center text-xs text-gray-400">You&apos;re all caught up.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
